import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import accounting from 'accounting';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, LoadingLarge, Theme, TypeAhead } from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { SiteHierarchyByRootSiteQuery, SiteNameTypeAheadQuery } from '../../graphql/queries';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import BasicDetails from './editing/basic-details';
import TransactionServices from './editing/transaction-services';
import Address from './editing/address';
import { EditSite } from '../../graphql/mutations';
import Relationships from './editing/relationships';
import { cleanModelDeep } from '../etl-helpers';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
  },
  mapContainer: {
    width: '100%',
    height: 400,
    border: `2px solid ${Theme.greyDark}`,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    '& h2': {
      marginRight: 'auto',
    },
  },
  settingsContainerInner: {
    margin: '15px',
    padding: 15,
    border: `2px ${Theme.grey80} solid`,
    borderRadius: 2,
    boxShadow: '0 1px 10px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

const sanitizeSite = (site) => JSON.parse(JSON.stringify(site));

const EditSiteDetails = ({ match, history }) => {
  const classes = useStyles();
  const orgSiteId = match.params.siteId;
  const locationId = match.params.locationId;
  const { data, loading } = useQuery(SiteHierarchyByRootSiteQuery, { variables: { orgSiteId } });
  const [saveSite, { loading: saving }] = useMutation(EditSite);
  const sitesList = useMemo(() => data?.siteHierarchyByRootSite || [], [data]);
  const [editingSite, setEditingSite] = useState({ _id: locationId, siteName: 'Site' });
  const [editedFields, setEditedFields] = useState([]);

  const orgSite = useMemo(() => {
    return sitesList.find((s) => s.siteType === 'ORGANIZATION');
  }, [sitesList]);

  useEffect(() => {
    if (sitesList.length > 0) {
      const location = sitesList.find((s) => s._id === locationId);
      const siteName = orgSite ? orgSite.siteName : 'Site';
      TitleService.setTitles('Inspecting Site Hierarchy');
      Breadcrumbs.set([
        {
          url: '/sites',
          title: 'Sites Administration',
        },
        {
          url: `/sites/${orgSiteId}`,
          title: `${siteName} Hierarchy`,
        },
        {
          url: `/sites/${orgSiteId}/${location._id}`,
          title: `Editing ${location.siteName}`,
        },
      ]);
      setEditingSite(sanitizeSite(location));
    }
  }, [sitesList, locationId, orgSite, orgSiteId]);

  const changeFieldValue = (fieldName, fieldValue) => {
    const newSite = { ...editingSite };
    const nameParts = fieldName.split('.');
    const editedField = nameParts[0];
    let currentScope = newSite;
    nameParts.slice(0, -1).forEach((p) => {
      // noopengrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
      if (typeof currentScope[p] === 'object') currentScope = currentScope[p];
    });
    currentScope[nameParts.pop()] = fieldValue;
    setEditingSite(cleanModelDeep(newSite));
    const fields = new Set(editedFields);
    fields.add(editedField);
    setEditedFields([...fields]);
  };

  const handleSearchType = (value) => {
    if (!value?.result) return;
    if (value.result.siteType === 'FACILITY') {
      history.push(`/sites/${value.result.rootSite}/${value.result._id}`);
    } else {
      history.push(`/sites/${value.result._id}/${value.result._id}`);
    }
  };

  const setFieldValue = (fieldType, fieldName, change) => {
    switch (fieldType) {
      case 'select':
        changeFieldValue(fieldName, change[0].value);
        break;
      case 'select-multiple':
        changeFieldValue(
          fieldName,
          change.map((o) => o.value),
        );
        break;
      case 'text':
        changeFieldValue(fieldName, change);
        break;
      case 'text[]':
        changeFieldValue(fieldName, change);
        break;
      case 'toggle':
        changeFieldValue(fieldName, change);
        break;
      case 'number':
        changeFieldValue(fieldName, accounting.unformat(change));
        break;
      default:
        changeFieldValue(fieldName, change);
    }
  };

  const handleSaveSite = () => {
    const saveObject = editedFields.reduce(
      (values, field) => {
        values[field] = editingSite[field];
        return values;
      },
      { _id: editingSite._id },
    );
    saveSite({
      variables: {
        site: saveObject,
      },
    }).then((result) => {
      if (result.data.editSite.errors.length > 0) throw new Error(result.data.editSite.errors[0]);
      setEditingSite(sanitizeSite(result.data.editSite.result));
    });
    setEditedFields([]);
  };

  if (loading || !editingSite?.siteType) return <LoadingLarge />;

  return (
    <div className={classes.wrap}>
      <TypeAhead query={SiteNameTypeAheadQuery} queryName="siteNameTypeAheadQuery" onChange={handleSearchType} />
      <div className={classes.header}>
        <h2>{editingSite.siteName}</h2>
        <Button disabled={editedFields.length === 0} onClick={handleSaveSite} loading={saving} loadingText="Saving...">
          Save Site
        </Button>
      </div>
      <div className={classes.settingsContainer}>
        <div className={classes.settingsContainerInner}>
          <h3>Basic Details</h3>
          <BasicDetails site={editingSite} setFieldValue={setFieldValue} />
        </div>
      </div>
      <div className={classes.settingsContainer}>
        <div className={classes.settingsContainerInner}>
          <h3>Address</h3>
          <Address site={editingSite} setFieldValue={setFieldValue} />
        </div>
      </div>
      {editingSite.siteRole.includes('SELLER') && editingSite.siteType !== 'FACILITY' && (
        <div className={classes.settingsContainer}>
          <div className={classes.settingsContainerInner}>
            <h3>Transaction Services</h3>
            <TransactionServices site={editingSite} setFieldValue={setFieldValue} />
          </div>
        </div>
      )}

      {editingSite.siteRole.includes('SELLER') && editingSite.siteType !== 'FACILITY' && (
        <div className={classes.settingsContainer}>
          <div className={classes.settingsContainerInner}>
            <h3>Relationships</h3>
            <Relationships site={editingSite} setFieldValue={setFieldValue} />
          </div>
        </div>
      )}
    </div>
  );
};

EditSiteDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      siteId: PropTypes.string,
      locationId: PropTypes.string,
    }),
  }),
};

export default EditSiteDetails;
